/* eslint-disable react/no-array-index-key */
import { toFlag } from 'pano-utils';
import type { CFModel3D } from '@types';
import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import { selectLibModal3d } from '@modules/mediaLib';
import { useCustomFeature } from '@modules/panel/CustomFeatureContext';
import SortableList from '@components/SortableList';
import Tip from '@components/Tip';
import { Validator } from '@components/Validator';
import arrayMove from '@utils/arrayMove';
import FeatureToggle from '@components/FeatureToggle';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { $teamMemberData } from '@stores/memberStores';
import { showConfirmModal } from '@components/ConfirmModal';
import Item from './Item';

const MAX = 10;

const typeItems: DropdownItem<0 | 1>[] = [
  { id: 0, name: '纯净模式' },
  { id: 1, name: '原生模式' },
];

function CModel3dSetting() {
  const { data, setData } = useCustomFeature<CFModel3D>();
  const { modelTids = [] } = data;

  const handleSelectModel = async () => {
    const teamMemberData = $teamMemberData.getState();
    if (teamMemberData.role !== 0) {
      await showConfirmModal({
        title: '温馨提示',
        buttonText: '我知道了',
        hideCancel: true,
        content: (
          <>
            <div>3D模型仅支持企业所有者操作</div>
            <div>请联系企业所有者进行添加操作</div>
          </>
        ),
      });
      return;
    }
    const { tids } = await selectLibModal3d({
      multiple: true,
      addTids: new Set(modelTids),
    });
    if (!tids) return;
    if (tids.length + modelTids.length > MAX) {
      tids.length = MAX - modelTids.length;
    }
    setData((draft) => {
      draft.modelTids.push(...tids);
    });
  };
  return (
    <>
      <Collapse title="3D模型设置">
        <Button className="w-full" onClick={handleSelectModel}>
          添加3D模型
        </Button>
        <div className="mt-20 mb-10 flex">
          <div>3D模型列表：</div>
          <div className="ml-10 self-end text-black-gray">{`${modelTids.length}/${MAX}`}</div>
        </div>
        <Validator
          value={modelTids}
          validFn={(value) => value.length > 0}
          warningMessage="请添加3D模型"
        >
          {({ value }) => {
            return value.length ? (
              <SortableList
                axis="y"
                lockAxis="y"
                distance={10}
                onSortEnd={(sort) => {
                  setData((draft) => {
                    draft.modelTids = arrayMove({
                      array: draft.modelTids,
                      fromIndex: sort.oldIndex,
                      toIndex: sort.newIndex,
                    });
                  });
                }}
              >
                <div>
                  {value.map((tid, i) => (
                    <Item
                      listIndex={i}
                      tid={tid}
                      key={`${i}-${tid}`}
                      onDelete={(index) => {
                        setData((draft) => {
                          draft.modelTids.splice(index, 1);
                        });
                      }}
                      index={i}
                    />
                  ))}
                </div>
              </SortableList>
            ) : (
              <>
                <Tip className="text-base">还未添加3D模型</Tip>
                <Tip className="text-base">{`最多支持添加${MAX}个3D模型`} </Tip>
              </>
            );
          }}
        </Validator>
      </Collapse>
      <Collapse title="播放控制">
        <FeatureToggle
          title="播放时关闭背景音乐"
          isOn={!!data.turnOffBgMusicOnPlay}
          onToggle={(val) =>
            setData((draft) => {
              draft.turnOffBgMusicOnPlay = toFlag(val);
            })
          }
        />
        <div className="mt-20 mb-10">3D模型显示方式</div>
        <Dropdown
          items={typeItems}
          onChange={(id) => {
            setData((draft) => {
              draft.displayStyle = id;
            });
          }}
          selected={data.displayStyle}
        />
        <div className="mt-10 text-sm text-orange">
          {data.displayStyle === 0
            ? '纯净模式仅保留3D模型原作品中的模型/贴图/动画等，以弹出层更纯净地展示模型'
            : '原生模式保留3D模型原作品大部分功能，仅对界面UI微调以便更好融合漫游中'}
        </div>
      </Collapse>
    </>
  );
}

export default CModel3dSetting;
